import type { DistortionServiceOptions } from "./distortion-processing";
import { serviceContainer } from "./serviceContainer";
import type {
  GetReversePixelMapperFactoryArgs,
  ReversePixelMapperFactoriesPoolKeyMap,
  ReversePixelMapper,
} from "./pixel-mapper";
import type { ImageAdapter } from "./image-adapter";

/**
 * Distorts image using distortion name and distortion arguments and returns distorted image resource.
 *
 * @param image
 * @param distortion
 * @param args
 * @param options
 */
export function distortUnwrap<
  ResourceType,
  K extends keyof ReversePixelMapperFactoriesPoolKeyMap
>(
  image: ResourceType | ImageAdapter<ResourceType>,
  distortion: K,
  args: GetReversePixelMapperFactoryArgs<K>,
  options?: DistortionServiceOptions
): Promise<ResourceType>;

/**
 * Distorts image using ReversePixelMapper instance and returns distorted image resource.
 *
 * @param image
 * @param reversePixelMapper
 * @param options
 */
export function distortUnwrap<ResourceType>(
  image: ResourceType | ImageAdapter<ResourceType>,
  reversePixelMapper: ReversePixelMapper,
  options?: DistortionServiceOptions
): Promise<ResourceType>;

/**
 * distortUnwrap() implementation
 *
 * @param image
 * @param distortionOrMapper
 * @param argsOrOptions
 * @param mayBeOptions
 */
export async function distortUnwrap<ResourceType>(
  image: ResourceType | ImageAdapter<ResourceType>,
  distortionOrMapper:
    | keyof ReversePixelMapperFactoriesPoolKeyMap
    | string
    | ReversePixelMapper,
  argsOrOptions: number[] | DistortionServiceOptions | undefined,
  mayBeOptions?: DistortionServiceOptions
): Promise<ResourceType> {
  const result = await serviceContainer
    .resolve("DistortionService")
    .distort(image, distortionOrMapper, argsOrOptions, mayBeOptions);
  return result.image.getResource();
}
